/**
 * i18n Module — Internationalization System
 * Supports: en, ko, ja, zh-Hant, es, pt-BR, th, id
 */

import { getRaw, setRaw } from '../persist.js';
import en from './en.js';
import ko from './ko.js';
import ja from './ja.js';
import zhHant from './zh-Hant.js';
import es from './es.js';
import ptBR from './pt-BR.js';
import th from './th.js';
import id from './id.js';
import type { TranslationKey, Translations } from './en.js';

export type Locale = 'en' | 'ko' | 'ja' | 'zh-Hant' | 'es' | 'pt-BR' | 'th' | 'id';

export const SUPPORTED_LOCALES: Locale[] = ['en', 'ko', 'ja', 'zh-Hant', 'es', 'pt-BR', 'th', 'id'];

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ko: '한국어',
  ja: '日本語',
  'zh-Hant': '繁體中文',
  es: 'Español',
  'pt-BR': 'Português',
  th: 'ภาษาไทย',
  id: 'Bahasa Indonesia',
};

const TRANSLATIONS: Record<Locale, Translations> = {
  en, ko, ja, 'zh-Hant': zhHant, es, 'pt-BR': ptBR, th, id,
};

const STORAGE_KEY = 'swordEnhance_locale';

let currentLocale: Locale = 'en';
const listeners: Array<() => void> = [];

function findLocaleMatch(langCode: string): Locale | null {
  const normalized = langCode.toLowerCase();

  const exact = SUPPORTED_LOCALES.find(
    (loc) => loc.toLowerCase() === normalized,
  );
  if (exact) return exact;

  const base = normalized.split('-')[0];
  const baseMatch = SUPPORTED_LOCALES.find(
    (loc) => loc.toLowerCase().split('-')[0] === base,
  );
  if (baseMatch) return baseMatch;

  return null;
}

function detectLocaleSync(): Locale {
  try {
    const saved = getRaw(STORAGE_KEY) as Locale | null;
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
  } catch { /* storage unavailable */ }

  try {
    const langs = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];
    for (const lang of langs) {
      const match = findLocaleMatch(lang);
      if (match) return match;
    }
  } catch { /* SSR-safe */ }

  return 'en';
}

async function enhanceWithDeviceApi(): Promise<void> {
  try {
    const saved = getRaw(STORAGE_KEY);
    if (saved) return;
  } catch { return; }

  try {
    const { Device } = await import('@capacitor/device');
    const { value } = await Device.getLanguageCode();
    if (value) {
      const match = findLocaleMatch(value);
      if (match && match !== currentLocale) {
        currentLocale = match;
        try {
          setRaw(STORAGE_KEY, match);
          document.documentElement.lang = match;
        } catch { /* */ }
        for (const fn of listeners) fn();
      }
    }
  } catch { /* Not on Capacitor */ }
}

export function initI18n(): Locale {
  currentLocale = detectLocaleSync();
  try { document.documentElement.lang = currentLocale; } catch { /* */ }
  enhanceWithDeviceApi();
  return currentLocale;
}

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(locale: Locale): void {
  if (!SUPPORTED_LOCALES.includes(locale)) return;
  currentLocale = locale;
  try {
    setRaw(STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  } catch { /* */ }
  for (const fn of listeners) fn();
}

export function onLocaleChange(fn: () => void): () => void {
  listeners.push(fn);
  return () => {
    const i = listeners.indexOf(fn);
    if (i >= 0) listeners.splice(i, 1);
  };
}

export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  let text: string =
    TRANSLATIONS[currentLocale]?.[key] ??
    TRANSLATIONS.en[key] ??
    key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return text;
}

export function getSupportedLocales(): Array<{ code: Locale; name: string }> {
  return SUPPORTED_LOCALES.map((code) => ({ code, name: LOCALE_NAMES[code] }));
}

export function showLanguagePicker(container?: HTMLElement): void {
  const el = container ?? document.body;
  if (!el) return;

  const overlay = document.createElement('div');
  overlay.style.cssText =
    'position:fixed;inset:0;background:rgba(0,0,0,0.7);' +
    'z-index:99999;display:flex;align-items:center;justify-content:center;';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  const box = document.createElement('div');
  box.style.cssText =
    'max-width:320px;width:86%;padding:22px;background:#1a1a2e;' +
    'border-radius:14px;border:2px solid #3a5a8a;color:#fff;' +
    'box-shadow:0 10px 40px rgba(0,0,0,0.6);';

  const title = document.createElement('div');
  title.style.cssText =
    'margin-bottom:14px;font-size:18px;font-weight:bold;text-align:center;';
  title.textContent = t('language');
  box.appendChild(title);

  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:8px;';

  for (const code of SUPPORTED_LOCALES) {
    const btn = document.createElement('button');
    const active = code === currentLocale;
    btn.style.cssText =
      'padding:11px 8px;border:2px solid ' +
      (active ? '#a855f7' : '#444') + ';' +
      'border-radius:8px;background:' +
      (active ? 'rgba(168,85,247,0.2)' : '#0e0e1a') + ';' +
      'color:#fff;font-size:13px;cursor:pointer;text-align:center;' +
      'font-family:inherit;';
    btn.textContent = LOCALE_NAMES[code] + (active ? ' ✓' : '');
    btn.addEventListener('click', () => {
      if (code !== currentLocale) {
        setLocale(code);
        window.location.reload();
      } else {
        overlay.remove();
      }
    });
    grid.appendChild(btn);
  }

  box.appendChild(grid);
  overlay.appendChild(box);
  el.appendChild(overlay);
}

export type { TranslationKey, Translations };
